import { Visit } from "@cur8/rich-entity";
import { ViewStack } from "@pomle/react-viewstack";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useAppInsights } from "render/context/AppInsightsContext";
import { usePatientConsentsQuery } from "render/hooks/api/queries/usePatientConsentsQuery";
import { usePatientQuery } from "render/hooks/api/queries/usePatientQuery";
import { useJoinConciergeSessionMutation } from "render/hooks/mutations/concierge/useJoinConciergeSessionMutation";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { CheckInFailView } from "./components/CheckInFailView";
import { CheckInFlowView } from "./components/CheckInFlowView";
import { Loader, LoaderStatus } from "./components/Loader";
import { isCheckedIn, useTodayVisitsQuery } from "./hooks/useTodayVisitsQuery";
import {
  AppointmentValidity,
  getAppointmentSlotValidity,
} from "./lib/getAppointmentSlotValidity";
import styles from "./styles.module.sass";
import { Trans } from "./trans";
import { useAppointmentQuery } from "render/hooks/api/queries/useAppointmentQuery";

interface ConciergeCheckInSessionViewProps {
  sessionId: string;
}

function hasCheckedInSlot(visits?: Visit[]) {
  return !!visits?.find(isCheckedIn);
}

enum ViewState {
  JoinSessionFail = "join-session-fail",
  QueryBookingsFail = "query-bookings-fail",
  QueryConsentsFail = "query-consents-fail",
  TodayNoSlots = "today-no-slots",
  Fetching = "fetching",
  CheckInEarly = "check-in-early",
  CheckInLate = "check-in-late",
  CheckInTimeOut = "check-in-timeout",
  Success = "Success",
  WaitingForCheckIn = "WaitingForCheckIn",
}

const TIME_OUT_TIME_SECONDS = 30;

interface StateRender {
  viewState: ViewState;
  status: LoaderStatus;
  component?: () => React.ReactNode;
}

export function ConciergeCheckInSessionView({
  sessionId,
}: ConciergeCheckInSessionViewProps) {
  const appInsights = useAppInsights();
  const [loaderActive, setLoaderActive] = useState(true);
  const patient = usePatientQuery();
  const patientConsents = usePatientConsentsQuery();
  const joinSessionMutation = useJoinConciergeSessionMutation();
  const todayVisitsQuery = useTodayVisitsQuery();
  const todaysVisit = todayVisitsQuery.data?.at(0);

  const appointmentQuery = useAppointmentQuery(
    { slotId: todaysVisit?.slotId },
    { enabled: !!todaysVisit?.slotId }
  );

  useEffect(() => {
    if (joinSessionMutation.data || joinSessionMutation.error) {
      return;
    }
    joinSessionMutation.mutate(sessionId);
  }, [joinSessionMutation, sessionId]);

  const sessionStart = useMemo(() => {
    return joinSessionMutation.data ? DateTime.now() : undefined;
  }, [joinSessionMutation.data]);

  const { status, component, viewState } = useMemo<StateRender>(() => {
    if (joinSessionMutation.error) {
      return {
        viewState: ViewState.JoinSessionFail,
        status: LoaderStatus.Fail,
        component: () => (
          <CheckInFailView
            Header={<Trans.FailToConnect.Header />}
            Text={<Trans.FailToConnect.Description />}
          />
        ),
      };
    }
    if (appointmentQuery.error) {
      return {
        viewState: ViewState.QueryBookingsFail,
        status: LoaderStatus.Fail,
        component: () => (
          <CheckInFailView
            Header={<Trans.FailToConnect.Header />}
            Text={<Trans.FailToConnect.Description />}
          />
        ),
      };
    }
    if (todayVisitsQuery.error) {
      return {
        viewState: ViewState.QueryBookingsFail,
        status: LoaderStatus.Fail,
        component: () => (
          <CheckInFailView
            Header={<Trans.FailToConnect.Header />}
            Text={<Trans.FailToConnect.Description />}
          />
        ),
      };
    }
    if (patientConsents.error) {
      return {
        viewState: ViewState.QueryBookingsFail,
        status: LoaderStatus.Fail,
        component: () => (
          <CheckInFailView
            Header={<Trans.FailToConnect.Header />}
            Text={<Trans.FailToConnect.Description />}
          />
        ),
      };
    }
    const joinSessionReady = !!joinSessionMutation.data;
    const todaysVisitsReady = !!todayVisitsQuery.data;
    const patientDataReady = !!patient.data;
    const patientConsentsReady = !!patientConsents.data;

    if (
      !todaysVisitsReady ||
      !joinSessionReady ||
      !patientDataReady ||
      !patientConsentsReady
    ) {
      return {
        viewState: ViewState.Fetching,
        status: LoaderStatus.Loading,
      };
    }

    if (
      todaysVisitsReady &&
      todayVisitsQuery.data.length > 0 &&
      !appointmentQuery.data
    ) {
      return {
        viewState: ViewState.Fetching,
        status: LoaderStatus.Loading,
      };
    }

    if (todayVisitsQuery.data.length === 0) {
      return {
        viewState: ViewState.TodayNoSlots,
        status: LoaderStatus.Fail,
        component: () => (
          <CheckInFailView
            Header={<Trans.NoAppointment.Header />}
            Text={<Trans.NoAppointment.Description />}
          />
        ),
      };
    }

    if (!hasCheckedInSlot(todayVisitsQuery.data)) {
      const appointmentValidity = getAppointmentSlotValidity(
        appointmentQuery.data
      );
      if (appointmentValidity === AppointmentValidity.EarlyArrival) {
        return {
          viewState: ViewState.CheckInEarly,
          status: LoaderStatus.Fail,
          component: () => (
            <CheckInFailView
              Header={<Trans.EarlyArrival.Header />}
              Text={<Trans.EarlyArrival.Description />}
            />
          ),
        };
      }
      if (appointmentValidity === AppointmentValidity.LateArrival) {
        return {
          viewState: ViewState.CheckInLate,
          status: LoaderStatus.Fail,
          component: () => (
            <CheckInFailView
              Header={<Trans.LateArrival.Header />}
              Text={<Trans.LateArrival.Description />}
            />
          ),
        };
      }

      if (
        sessionStart &&
        Math.abs(sessionStart.diffNow("second").seconds) > TIME_OUT_TIME_SECONDS
      ) {
        return {
          viewState: ViewState.CheckInTimeOut,
          status: LoaderStatus.Fail,
          component: () => (
            <CheckInFailView
              Header={<Trans.UnableToCheckIn.Header />}
              Text={<Trans.UnableToCheckIn.Description />}
            />
          ),
        };
      }
      return {
        viewState: ViewState.WaitingForCheckIn,
        status: LoaderStatus.Loading,
      };
    }

    return {
      viewState: ViewState.Success,
      status: LoaderStatus.Complete,
      component: () => (
        <CheckInFlowView
          updateConsents={patientConsents.refetch}
          patient={patient.data}
          consents={patientConsents.data}
        />
      ),
    };
  }, [
    joinSessionMutation.error,
    joinSessionMutation.data,
    appointmentQuery.error,
    appointmentQuery.data,
    todayVisitsQuery.error,
    todayVisitsQuery.data,
    patientConsents.error,
    patientConsents.data,
    patientConsents.refetch,
    patient.data,
    sessionStart,
  ]);

  useEffect(() => {
    appInsights.trackEvent({
      name: "concierge-check-in",
      properties: { checkInState: viewState },
    });
  }, [viewState, appInsights]);

  return (
    <FullScreenPageLayout>
      <div className={styles.ConciergeCheckInView} data-status={status}>
        <ViewStack>
          <section data-active={loaderActive}>
            <div className={styles.body}>
              <div className={styles.content}>
                <Loader status={status} onDone={() => setLoaderActive(false)} />
              </div>
            </div>
          </section>
          <section data-active={!loaderActive}>{component?.()}</section>
        </ViewStack>
      </div>
    </FullScreenPageLayout>
  );
}
